/**
 * Restaurant Clustering Utility
 *
 * Groups nearby restaurants based on zoom level to prevent marker overlap.
 * Adapted from GeographicHeatmap.js clustering approach.
 */

import getDistance from "geolib/es/getDistance";
import { getLeafRating } from "./leafRating";

/**
 * Calculate the clustering radius based on zoom level
 * At higher zoom levels (zoomed in), clustering radius is smaller
 * At lower zoom levels (zoomed out), clustering radius is larger
 *
 * @param {number} zoom - Map zoom level (typically 1-20)
 * @returns {number} Clustering radius in meters
 */
export function getClusterRadius(zoom) {
  // Base radius that scales with zoom
  // At zoom 12 (typical city view): ~500m
  // At zoom 15 (neighborhood): ~100m
  // At zoom 18 (street level): ~25m
  const baseRadius = 500000; // meters at zoom 0
  return baseRadius / Math.pow(2, zoom);
}

/**
 * Create a cluster from a group of restaurants
 *
 * @param {Array} restaurants - Array of restaurants in this cluster
 * @returns {Object} Cluster object with aggregated data
 */
function createCluster(restaurants) {
  // Calculate center as average of all coordinates
  const centerLat = restaurants.reduce(
    (sum, r) => sum + r.geoJson.coordinates[1],
    0
  ) / restaurants.length;
  const centerLng = restaurants.reduce(
    (sum, r) => sum + r.geoJson.coordinates[0],
    0
  ) / restaurants.length;

  // Calculate average leaf count
  const totalLeafCount = restaurants.reduce((sum, r) => {
    const rating = getLeafRating(r);
    return sum + rating.count;
  }, 0);
  const avgLeafCount = Math.round(totalLeafCount / restaurants.length);

  return {
    isCluster: true,
    id: `cluster-${restaurants.map((r) => r._id).join("-")}`,
    restaurants: restaurants,
    count: restaurants.length,
    avgLeafCount: avgLeafCount,
    center: {
      lat: centerLat,
      lng: centerLng,
    },
    // Bounds for zooming into cluster
    bounds: calculateBounds(restaurants),
  };
}

/**
 * Calculate bounds that contain all restaurants in a group
 *
 * @param {Array} restaurants - Array of restaurants
 * @returns {Object} Bounds object with north, south, east, west
 */
function calculateBounds(restaurants) {
  let north = -90;
  let south = 90;
  let east = -180;
  let west = 180;

  restaurants.forEach((r) => {
    const lat = r.geoJson.coordinates[1];
    const lng = r.geoJson.coordinates[0];

    if (lat > north) north = lat;
    if (lat < south) south = lat;
    if (lng > east) east = lng;
    if (lng < west) west = lng;
  });

  // Add a small padding
  const latPadding = (north - south) * 0.1 || 0.001;
  const lngPadding = (east - west) * 0.1 || 0.001;

  return {
    north: north + latPadding,
    south: south - latPadding,
    east: east + lngPadding,
    west: west - lngPadding,
  };
}

/**
 * Cluster restaurants based on proximity and zoom level
 *
 * @param {Array} restaurants - Array of restaurant objects
 * @param {number} zoom - Current map zoom level
 * @param {Object} bounds - Optional map bounds to filter visible restaurants
 * @returns {Object} { clusters: [], singles: [] }
 */
export function clusterRestaurants(restaurants, zoom, bounds = null) {
  if (!restaurants || restaurants.length === 0) {
    return { clusters: [], singles: [] };
  }

  // Filter to visible restaurants if bounds provided
  let visibleRestaurants = restaurants;
  if (bounds && bounds.nw && bounds.se) {
    visibleRestaurants = restaurants.filter((r) => {
      const lat = r.geoJson.coordinates[1];
      const lng = r.geoJson.coordinates[0];
      return (
        lat <= bounds.nw.lat &&
        lat >= bounds.se.lat &&
        lng >= bounds.nw.lng &&
        lng <= bounds.se.lng
      );
    });
  }

  // At very high zoom levels (18+), don't cluster
  if (zoom >= 18) {
    return { clusters: [], singles: visibleRestaurants };
  }

  const clusterRadius = getClusterRadius(zoom);
  const processed = new Set();
  const clusters = [];
  const singles = [];

  visibleRestaurants.forEach((restaurant) => {
    if (processed.has(restaurant._id)) return;

    // Find all nearby restaurants
    const nearby = visibleRestaurants.filter((other) => {
      if (processed.has(other._id)) return false;
      if (other._id === restaurant._id) return true;

      const distance = getDistance(
        { lat: restaurant.geoJson.coordinates[1], lng: restaurant.geoJson.coordinates[0] },
        { lat: other.geoJson.coordinates[1], lng: other.geoJson.coordinates[0] }
      );

      return distance <= clusterRadius;
    });

    // Mark all as processed
    nearby.forEach((r) => processed.add(r._id));

    if (nearby.length >= 2) {
      // Create a cluster
      clusters.push(createCluster(nearby));
    } else {
      // Single restaurant
      singles.push(restaurant);
    }
  });

  return { clusters, singles };
}

/**
 * Get zoom level needed to spread a cluster
 *
 * @param {Object} cluster - Cluster object
 * @param {number} currentZoom - Current map zoom level
 * @returns {number} Target zoom level
 */
export function getZoomToSpreadCluster(cluster, currentZoom) {
  // Calculate the span of the cluster
  const latSpan = cluster.bounds.north - cluster.bounds.south;
  const lngSpan = cluster.bounds.east - cluster.bounds.west;
  const maxSpan = Math.max(latSpan, lngSpan);

  // Estimate zoom needed to show all points spread out
  // Each zoom level doubles the map scale
  let targetZoom = currentZoom;

  // Add 2-3 zoom levels to spread the cluster nicely
  targetZoom = Math.min(currentZoom + 3, 18);

  return targetZoom;
}
